import {defineStore} from 'pinia';
import * as api from "../services/api";
import jp from "jsonpath";
import {useCampaignStore} from "./CampaignStore";
import {usePresenceStore} from "./PresenceStore";
import {t} from "@/i18n";


export const useComponentEditStore = defineStore('ComponentEditStore', {
    state: () => {
        return {
            processing: false,
            showEditDialog: false,
            label: null,
            campaign: {},
            content: {},
            editingField: 'content',
            editor_config: {},
            window_items: [],
            current_window: 0,
            current_window_props: null,
            window_items_opened: [],
            window_props_history: [],
            editingRevisionField: null,
            baseRevision: null,
            json_path: null,

            // ----------------------------------------------------------------------------
            // Session / Editor Timeout
            // ----------------------------------------------------------------------------
            sessionExpired: false,
            sessionExpiredMessage: null,

            // Optional: errors by json path or field
            errors: {},
        };
    },
    getters: {},
    actions: {
        reset() {
            this.processing = false;
            this.showEditDialog = false;
            this.label = null;
            this.content = {};
            this.editingField = 'content';
            this.editor_config = {};
            this.window_items = [];
            this.current_window = 0;
            this.current_window_props = null;
            this.window_items_opened = [];
            this.window_props_history = [];
            this.editingRevisionField = null;
            this.baseRevision = null;
            this.json_path = null;
            this.sessionExpired = false;
            this.sessionExpiredMessage = null;
            this.errors = {};
        },
        /**
         * Handle expired editor/session responses.
         *
         * Expected backend response:
         * {
         *     message: 'Deine Bearbeitungssitzung ist abgelaufen...',
         *     reason: 'fresh_session_required' | 'editor_idle_timeout'
         * }
         *
         * @param {Object} error
         * @param {Object} options
         * @param {boolean} options.reload
         * @returns {boolean}
         */
        handleExpiredEditorSession(error, options = {reload: true}) {
            const status = error.response?.status;
            const reason = error.response?.data?.reason;

            const sessionExpiredReasons = [
                'session_expired',
                'fresh_session_required',
                'editor_idle_timeout',
            ];

            const method = error.config?.method?.toLowerCase();
            const isMutatingRequest = ['post', 'put', 'patch', 'delete'].includes(method);

            const isNetworkSessionError = !error.response
                && error.code === 'ERR_NETWORK'
                && isMutatingRequest;

            const isExpired = status === 419
                || status === 401
                || sessionExpiredReasons.includes(reason)
                || isNetworkSessionError;

            if (!isExpired) {
                return false;
            }

            if (this.sessionExpired) {
                return true;
            }

            this.processing = false;
            this.showEditDialog = false;
            this.sessionExpired = true;
            this.sessionExpiredMessage = error.response?.data?.message
                || t('landingpage.editor.session_expired');

            if (options.reload) {
                window.setTimeout(() => {
                    window.location.reload();
                }, 1500);
            }

            return true;
        },
        parseEditorConfig() {
            try {
                this.window_items = [];
                if (this.editor_config && this.editor_config.items) {
                    this.extractSubPanel(this.editor_config.items, this.window_items);
                } else {
                    this.editor_config = null;
                }
            } catch (e) {
                console.log(e);
                this.window_items = [];
            }

        },
        /**
         * extract all sub panels and create a flat array for v-window/v-window-item usage
         *
         * Recursive Function!
         *
         * @param elements
         * @param panels
         * @returns {number}
         */
        extractSubPanel(elements, panels) {
            const items = [];
            let panel_index = panels.push(items) - 1;

            elements.forEach(e => {
                if (e.component === 'SubPanel') {
                    items.push({
                        "component": "SubPanel",
                        "label": e.label,
                        "hint": e.hint,
                        "json_path": e.json_path,
                        "component_config": {
                            ...e.component_config,
                            "window_target_index": this.extractSubPanel(e.items, panels),
                        },
                        "vuetify_config": e.vuetify_config,
                    });
                } else {
                    items.push(e);
                }
            });

            return panel_index;
        },
        /**
         * Reset the sub panel navigation.
         *
         * The window state belongs to *one* editor (content or info). When the
         * user switches between them, we have to start at the top level again.
         */
        resetWindows() {
            this.current_window = 0;
            this.current_window_props = null;
            this.window_items_opened = [];
            this.window_props_history = [];
        },
        openSubPanel(panel_props) {
            this.window_items_opened.unshift(panel_props.window_index);
            this.current_window_props = panel_props;
            this.window_props_history.unshift(panel_props);

            // For Vuetify
            this.current_window = panel_props.window_index;
        },
        closeSubPanel() {
            // Shift current
            this.window_props_history.shift();
            this.window_items_opened.shift();

            // Set to previous settings
            this.current_window_props = this.window_props_history[0] || null;
            this.current_window = this.window_items_opened[0] || 0;
        },
        /**
         * Einen Komponenten-Editor öffnen.
         *
         * **Die Sperre.** Der Merge auf dem Server behandelt Listen als
         * Block: Ändert sich irgendetwas in `textSection`, gilt die ganze
         * Liste als eine Änderung. Zwei Leute an verschiedenen
         * Textabschnitten derselben Seite bekommen deshalb beim Speichern
         * einen harten Konflikt, und der Zweite verliert seine Arbeit.
         *
         * Solange sich nicht gezielt nur der geänderte Eintrag übertragen
         * lässt, hat die Sperre denselben Zuschnitt wie der Konflikt -- die
         * ganze Liste. Dann erfährt der Zweite es vorher statt hinterher.
         *
         * @see resources/js/utils/editLock.mjs
         * @returns {boolean} ob der Editor geöffnet wurde
         */
        openEdit(patch_payload) {
            const campaignStore = useCampaignStore();
            const presenceStore = usePresenceStore();

            const json_path = patch_payload?.json_path
                || patch_payload?.jsonPath
                || patch_payload?.path
                || null;

            const belegt = json_path ? presenceStore.conflictingFocusFor(json_path) : null;

            if (belegt) {
                campaignStore.snackbarText = belegt.user?.fullName
                    ? t('landingpage.editor.locked_by', {name: belegt.user.fullName})
                    : t('landingpage.editor.locked');
                campaignStore.showSnackbar = true;

                return false;
            }

            this.reset();

            const revisionField = this.resolveRevisionFieldFromPayload(patch_payload);

            this.$patch(patch_payload);

            this.editingRevisionField = revisionField;
            this.baseRevision = campaignStore.getRevision(revisionField);
            this.json_path = json_path;

            this.showEditDialog = true;
            this.parseEditorConfig();

            /*
             * Ab jetzt sieht jeder andere den Bereich als belegt. Die
             * Markierung geht über denselben Weg wie die Feldmarkierungen im
             * Formular.
             */
            if (json_path) {
                presenceStore.fieldFocused(json_path);
            }

            return true;
        },
        closeEdit() {
            /* Erst freigeben, dann schliessen -- reset() räumt json_path weg. */
            if (this.json_path) {
                usePresenceStore().fieldBlurred(this.json_path);
            }

            this.showEditDialog = false;
        },
        /**
         * Saves the top-level campaign field that contains the changed JSON path.
         *
         * Example:
         * $.campaign.components.banner
         *
         * field: components
         * value: this.campaign.components
         *
         * This matches CampaignController@update(), which expects:
         * {
         *     field: 'components',
         *     value: {...}
         * }
         *
         * @param {Object|string} jsonPath
         * @returns {Promise<*>}
         */
        save(jsonPath) {
            const path = typeof jsonPath === 'string'
                ? jsonPath
                : jsonPath?.value;

            /*
             * Die drei Ablehnungen hier sind Programmierfehler (falscher
             * JSONPath im Aufrufer) und landen nur in console.error, siehe
             * handleImageSaveError(). Deshalb nicht übersetzt.
             */
            if (!path) {
                return Promise.reject({
                    message: 'Kein gültiger JSONPath zum Speichern vorhanden.', // i18n-ignore
                });
            }

            const parsed = jp.parse(path);

            /**
             * Expected paths:
             * $.campaign.components.banner
             * $.campaign.meta
             * $.campaign.legals
             *
             * parsed[0] = $
             * parsed[1] = campaign
             * parsed[2] = components | meta | legals | ...
             */
            const campaignKey = parsed[1]?.expression?.value;
            const field = parsed[2]?.expression?.value;

            if (campaignKey !== 'campaign' || !field) {
                return Promise.reject({
                    message: `Ungültiger Campaign JSONPath: ${path}`, // i18n-ignore
                });
            }

            const value = this.campaign[field];

            if (typeof value === 'undefined') {
                return Promise.reject({
                    message: `Das Feld "${field}" konnte in der Kampagne nicht gefunden werden.`, // i18n-ignore
                    field,
                    json_path: path,
                });
            }

            const payload = {
                field,
                value,
            };

            this.processing = true;

            return new Promise((resolve, reject) => {
                const url = window.route('campaigns.update', {
                    campaign: this.campaign.id,
                });

                api.client.patch(url, payload)
                    .then(response => {
                        this.processing = false;

                        if (response.data?.field) {
                            delete this.errors[response.data.field];
                        }

                        /**
                         * The backend returns the saved value.
                         * Keep the local campaign field in sync with the backend response.
                         */
                        if (
                            Object.prototype.hasOwnProperty.call(response.data || {}, 'value')
                            && field in this.campaign
                        ) {
                            this.campaign[field] = response.data.value;
                        }

                        resolve(response.data);
                    })
                    .catch(error => {
                        if (this.handleExpiredEditorSession(error)) {
                            reject(error.response?.data || error);
                            return;
                        }

                        this.processing = false;

                        /**
                         * Existing backend validation errors from Campaign::handleChange()
                         * may differ depending on the Change serialization.
                         * Therefore we store the safest possible shape.
                         */
                        if (error.response?.data) {
                            this.errors[field] = {
                                value: error.response.data.value || value,
                                errors: error.response.data.errors || error.response.data,
                            };
                        }

                        reject(error.response?.data || error);
                    });
            });
        },
        getAssetUploadUrl() {
            return window.route('assets.storeForCampaign', {campaign: this.campaign.id});
        },
        getAssetDeleteUrl(payload) {
            return window.route('assets.deleteCustom', {
                campaign: this.campaign.id,
                ...payload,
            });
        },
        getFieldFromJsonPath(path) {
            if (!path) {
                return null;
            }

            const parsed = jp.parse(path);
            const campaignKey = parsed[1]?.expression?.value;
            const field = parsed[2]?.expression?.value;

            if (campaignKey !== 'campaign' || !field) {
                return null;
            }

            return field;
        },
        getValidRevisionFields() {
            return [
                'meta',
                'components',
                'schedule',
                'legals',
                'page_settings',
                'display_settings',
            ];
        },
        resolveRevisionFieldFromPayload(patch_payload) {
            const validRevisionFields = this.getValidRevisionFields();

            /**
             * Best case: the caller explicitly tells us which Campaign field is protected.
             */
            if (
                patch_payload?.revisionField
                && validRevisionFields.includes(patch_payload.revisionField)
            ) {
                return patch_payload.revisionField;
            }

            /**
             * Second best: derive from JSONPath if present.
             */
            const path = patch_payload?.json_path
                || patch_payload?.jsonPath
                || patch_payload?.path
                || null;

            const fieldFromPath = this.getFieldFromJsonPath(path);

            if (fieldFromPath && validRevisionFields.includes(fieldFromPath)) {
                return fieldFromPath;
            }

            /**
             * Third best: use editingField if it is actually a Campaign field.
             *
             * Example from your logs:
             * editingField: 'meta'
             */
            if (
                patch_payload?.editingField
                && validRevisionFields.includes(patch_payload.editingField)
            ) {
                return patch_payload.editingField;
            }

            /**
             * Fallback for regular landingpage component editors.
             *
             * Your Call-To-Action payload currently has no editingField and no json_path.
             * Those editors modify campaign.components.
             */
            return 'components';
        },
        syncBaseRevisionFromResponse(responseData = {}, fallbackField = null) {
            const campaignStore = useCampaignStore();

            const field = responseData.field
                || fallbackField
                || this.editingRevisionField;

            if (!field) {
                return;
            }

            const revision = responseData.revision
                ?? responseData[`${field}_revision`]
                ?? responseData.change_revisions?.[field]
                ?? campaignStore.getRevision(field);

            campaignStore.setRevision(field, revision);

            if (this.editingRevisionField === field || !this.editingRevisionField) {
                this.editingRevisionField = field;
                this.baseRevision = campaignStore.getRevision(field);
            }
        },
        syncBaseRevisionFromCampaignResponse(responseData = {}, fallbackField = null) {
            const campaignStore = useCampaignStore();

            const field = responseData.field
                || fallbackField
                || this.editingRevisionField;

            if (!field) {
                return;
            }

            const revision = responseData.revision
                ?? responseData[`${field}_revision`]
                ?? responseData.change_revisions?.[field]
                ?? campaignStore.getRevision(field);

            campaignStore.setRevision(field, revision);

            if (this.editingRevisionField === field || !this.editingRevisionField) {
                this.editingRevisionField = field;
                this.baseRevision = campaignStore.getRevision(field);
            }
        },
    }
});
